// PATH: src/components/modules/devtools/index.js

import React, { useEffect, useState } from "react";
import DevIdePanel from "./panels/DevIdePanel";
import ImportZipDialog from "./components/ImportZipDialog";
import ChangesPanel from "./panels/ChangesPanel";
import ErrorLogPanel from "./panels/ErrorLogPanel";
import DocsPage from "../docs/DocsPage";
import FullChangePanel from "./full_change/panels/FullChangePanel";
import ReleaseSyncPanel from "./panels/ReleaseSyncPanel";
import PlannerPanel from "./planner/panels/PlannerPanel";
import LabPanel from "./lab/panels/LabPanel";
import DbExplorerPanel from "./panels/DbExplorerPanel";

export { DevIdePanel, ChangesPanel };

const ACTIVE_TAB_STORAGE_KEY = "devtools.activeTab.v1";
const VALID_TABS = new Set([
  "development", "changes", "fullChange", "releaseSync", "planner",
  "lab", "db", "errorLog", "docs",
]);

const getRestoredTab = () => {
  try {
    const saved = typeof window !== "undefined" ? window.localStorage.getItem(ACTIVE_TAB_STORAGE_KEY) : null;
    return VALID_TABS.has(saved) ? saved : "development";
  } catch (_error) {
    // Restricted browser storage must not prevent DevTools from opening.
    return "development";
  }
};

export default function DevToolsRoot() {
  const [tab, setTab] = useState(getRestoredTab);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [fullChangeImport, setFullChangeImport] = useState(null);
  const [fullChangeImportSavedVersion, setFullChangeImportSavedVersion] = useState(0);
  const [planningDocumentFocus, setPlanningDocumentFocus] = useState(null);

  useEffect(() => {
    try {
      window.localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, tab);
    } catch (_error) {
      // The tab remains usable when browser storage is unavailable.
    }
  }, [tab]);

  const openPlanningDocument = ({ slug, projectId, showHistory = false }) => {
    if (!slug || !projectId) return;
    setPlanningDocumentFocus({ slug, projectId, showHistory, requestId: Date.now() });
    setTab("docs");
  };

  const handleFullChangeImportReturnedPackage = async (context) => {
    if (!context || !context.changeId || !context.fullChangeId) {
      throw new Error("A linked DEV IDE change and Full Change are required to import a returned package.");
    }

    // Resolve the selected child before opening the unchanged Development ZIP modal.
    // Never fall back to the previously active change on failed resolution.
    const projectId = String(context.projectId || selectedProjectId || "default");
    const params = new URLSearchParams({ project_id: projectId });
    const changeResponse = await fetch(
      `/api/dev-ide/changes/${encodeURIComponent(context.changeId)}?${params.toString()}`,
    );
    const changeData = await changeResponse.json();
    const change = changeData.change;
    if (!changeResponse.ok || !changeData.success || !change || String(change.id) !== String(context.changeId)) {
      throw new Error(changeData.error || "Could not verify the linked DEV IDE change for import.");
    }
    if (String(change.project_id || "default") !== projectId) {
      throw new Error("The selected DEV IDE change belongs to a different project. Import was not opened.");
    }

    // Confirm membership in this Full Change rather than trusting a stale queue row.
    const linkedResponse = await fetch(
      `/api/dev-ide/full-change/linked-changes?full_change_id=${encodeURIComponent(context.fullChangeId)}`,
    );
    const linkedData = await linkedResponse.json();
    if (!linkedResponse.ok || !linkedData.success ||
        !(linkedData.linked_changes || []).some((row) => String(row.id) === String(change.id))) {
      throw new Error(linkedData.error || "The selected change is no longer linked to this Full Change. Import was not opened.");
    }

    // Match Development's active-change selection before enabling import/save.
    const activeResponse = await fetch("/api/dev-ide/active-change", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ change_id: change.id, project_id: projectId }),
    });
    const activeData = await activeResponse.json();
    const activeChange = activeData.active_change;
    if (!activeResponse.ok || !activeData.success || !activeChange ||
        String(activeChange.id) !== String(change.id) ||
        String(activeChange.project_id || "default") !== projectId) {
      throw new Error(activeData.error || "Failed to activate the selected change. Import was not opened.");
    }

    setSelectedProjectId(projectId);
    setFullChangeImport({ context, projectId, activeChange });
    // Deliberately keep the Full Change panel mounted and its selection intact.
  };

  const handleFullChangeImportWorkflowStep = (stepKey, state = "success", message = "") => {
    if (!fullChangeImport) return;
    fetch("/api/dev-ide/workflow-status/step", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        project_id: fullChangeImport.projectId,
        change_id: fullChangeImport.activeChange.id,
        step_key: stepKey,
        state,
        message: message || "",
      }),
    }).catch(() => {
      // Status reporting is non-blocking, as it is in the Development import flow.
    });
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Tabs */}
      <div
        style={{
          display: "flex",
          borderBottom: "1px solid #ddd",
          marginBottom: 8,
          overflowX: "auto",
          whiteSpace: "nowrap",
        }}
      >
        <div
          data-testid="devtools-tab-development"
          onClick={() => setTab("development")}
          style={{
            padding: "8px 16px",
            cursor: "pointer",
            borderBottom: tab === "development" ? "2px solid #1976d2" : "none",
          }}
        >
          Development
        </div>

        <div
          onClick={() => setTab("changes")}
          style={{
            padding: "8px 16px",
            cursor: "pointer",
            borderBottom: tab === "changes" ? "2px solid #1976d2" : "none",
          }}
        >
          Changes
        </div>

        <div
          data-testid="devtools-tab-full-change"
          onClick={() => setTab("fullChange")}
          style={{
            padding: "8px 16px",
            cursor: "pointer",
            borderBottom: tab === "fullChange" ? "2px solid #1976d2" : "none",
          }}
        >
          Full Change
        </div>

        <div
          onClick={() => setTab("releaseSync")}
          style={{
            padding: "8px 16px",
            cursor: "pointer",
            borderBottom: tab === "releaseSync" ? "2px solid #1976d2" : "none",
          }}
        >
          Release Sync
        </div>

        {/* FE-001: Planner */}
        <div
          onClick={() => setTab("planner")}
          style={{
            padding: "8px 16px",
            cursor: "pointer",
            borderBottom: tab === "planner" ? "2px solid #1976d2" : "none",
          }}
        >
          Planner
        </div>

        <div
          onClick={() => setTab("lab")}
          style={{
            padding: "8px 16px",
            cursor: "pointer",
            borderBottom: tab === "lab" ? "2px solid #1976d2" : "none",
          }}
        >
          Lab
        </div>

        <div
          onClick={() => setTab("db")}
          style={{
            padding: "8px 16px",
            cursor: "pointer",
            borderBottom: tab === "db" ? "2px solid #1976d2" : "none",
          }}
        >
          DB
        </div>

        <div
          onClick={() => setTab("errorLog")}
          style={{
            padding: "8px 16px",
            cursor: "pointer",
            borderBottom: tab === "errorLog" ? "2px solid #1976d2" : "none",
          }}
        >
          Error Log
        </div>

        <div
          onClick={() => setTab("docs")}
          style={{
            padding: "8px 16px",
            cursor: "pointer",
            borderBottom: tab === "docs" ? "2px solid #1976d2" : "none",
          }}
        >
          Docs
        </div>
      </div>

      {/* Panels */}
      <div style={{ flex: 1, minHeight: 0 }}>
        {tab === "development" && (
          <DevIdePanel
            selectedProjectId={selectedProjectId}
            setSelectedProjectId={setSelectedProjectId}
          />
        )}
        {tab === "changes" && (
          <ChangesPanel
            selectedProjectId={selectedProjectId}
            onSelectedProjectChange={setSelectedProjectId}
          />
        )}
        {tab === "fullChange" && (
          <FullChangePanel
            selectedProjectId={selectedProjectId}
            onSelectedProjectChange={setSelectedProjectId}
            onImportReturnedPackage={handleFullChangeImportReturnedPackage}
            onOpenPlanningDocument={openPlanningDocument}
            importSavedVersion={fullChangeImportSavedVersion}
          />
        )}
        {tab === "releaseSync" && (
          <ReleaseSyncPanel
            selectedProjectId={selectedProjectId}
            onSelectedProjectChange={setSelectedProjectId}
          />
        )}
        {tab === "planner" && <PlannerPanel />}
        {tab === "lab" && <LabPanel />}
        {tab === "db" && <DbExplorerPanel />}
        {tab === "errorLog" && <ErrorLogPanel />}
        {tab === "docs" && <DocsPage focusDocument={planningDocumentFocus} />}
      </div>
      {fullChangeImport && (
        <ImportZipDialog
          open={true}
          onClose={() => setFullChangeImport(null)}
          selectedProjectId={fullChangeImport.projectId}
          activeChange={fullChangeImport.activeChange}
          onImportedSaved={() => setFullChangeImportSavedVersion((version) => version + 1)}
          onWorkflowStep={handleFullChangeImportWorkflowStep}
          includeDocuments={fullChangeImport.context.includeDocuments !== false}
          initialZipPath={fullChangeImport.context.initialZipPath || ""}
          autoImportFromPath={Boolean(fullChangeImport.context.autoImportFromPath)}
          codexHandoffContext={fullChangeImport.context.codexHandoffContext || null}
          importContext={fullChangeImport.context}
        />
      )}
    </div>
  );
}
